import type {
  FindRegistryItemsResult,
  RegistryCreateItemOutput,
  RegistryItemCategoriesResult,
  RegistryItemContent,
  RegistryItemDetail,
  RegistryItemPreviewResult,
  RegistryItemTagsResult,
} from "schemas/generated/registry";
import type {
  RegistryItemType,
  RegistryItemVisibility,
} from "schemas/generated/registryCatalog";
import type { registryCreateItemInputSchema } from "schemas/registry";
import type { z } from "zod";
import type { RequestFn } from "./request";

export type RegistryItemsFindInput = {
  id?: string;
  slug?: string;
  type?: RegistryItemType;
  visibility?: RegistryItemVisibility;
  search?: string;
  searchMode?: "semantic" | "text";
  category?: string;
  tags?: string[];
  cursor?: string;
  limit?: number;
  withContent?: boolean;
};

export type RegistryItemsFindSourceInput = {
  projectId: string;
  sourceType: string;
  sourceKey: string;
  type?: RegistryItemType;
  visibility?: RegistryItemVisibility;
  limit?: number;
};

export type RegistryItemsFindFacetsInput = { type: RegistryItemType };

export type RegistryItemsGetInput = { id: string };

export type RegistryItemsGetContentInput = { id: string };

/**
 * Body for `POST /api/v1/registry/items`. The `id` is generated client-side
 * (so the caller can reference it before the round trip completes).
 *
 * Derived via `z.input` (not the generated type, which is the parsed output
 * shape) so fields with server-side defaults stay optional for callers. The
 * type-only schema import is erased at compile time, so the SDK takes no
 * runtime dependency on zod.
 */
export type CreateRegistryItemInput = z.input<
  typeof registryCreateItemInputSchema
>["item"];

export function createRegistryClient({ request }: { request: RequestFn }) {
  return {
    items: {
      find: (
        input: RegistryItemsFindInput,
      ): Promise<FindRegistryItemsResult> => {
        const { tags, ...rest } = input;
        return request<FindRegistryItemsResult>({
          method: "GET",
          path: "/api/v1/registry/items",
          query: {
            ...rest,
            ...(tags?.length ? { tags: JSON.stringify(tags) } : {}),
          },
        });
      },
      // Source-scoped lookup of a project's own artifacts (including unlisted
      // rows); the project is an explicit, authorized input.
      findSource: (
        input: RegistryItemsFindSourceInput,
      ): Promise<FindRegistryItemsResult> => {
        return request<FindRegistryItemsResult>({
          method: "GET",
          path: "/api/v1/registry/items/source",
          query: input,
        });
      },
      // Exhaustive category facets for a registry type. Public visibility
      // only; counts are intentionally not returned — callers only need the
      // value set today.
      findCategories: (
        input: RegistryItemsFindFacetsInput,
      ): Promise<RegistryItemCategoriesResult> => {
        return request<RegistryItemCategoriesResult>({
          method: "GET",
          path: "/api/v1/registry/items/categories",
          query: { type: input.type },
        });
      },
      // Exhaustive tag facets for a registry type. Same contract as
      // `findCategories`.
      findTags: (
        input: RegistryItemsFindFacetsInput,
      ): Promise<RegistryItemTagsResult> => {
        return request<RegistryItemTagsResult>({
          method: "GET",
          path: "/api/v1/registry/items/tags",
          query: { type: input.type },
        });
      },
      // DB-backed single-item read. Returns the registry row plus the
      // rendered `previewHtml`, which lives on the Postgres row. Use this
      // for preview-only surfaces (template cards, modal previews) so they
      // don't pull the full R2 file tree. Callers that only know a slug
      // should resolve it to an `id` via `items.find` first.
      get: (input: RegistryItemsGetInput): Promise<RegistryItemDetail> => {
        return request<RegistryItemDetail>({
          method: "GET",
          path: `/api/v1/registry/items/${encodeURIComponent(input.id)}`,
        });
      },
      // R2-backed content read. Returns the file tree and npm dependencies
      // used by install / edit / download flows. The rendered `previewHtml`
      // is not on this response — preview surfaces should call `items.get`
      // instead.
      getContent: (
        input: RegistryItemsGetContentInput,
      ): Promise<RegistryItemContent> => {
        return request<RegistryItemContent>({
          method: "GET",
          path: `/api/v1/registry/items/${encodeURIComponent(input.id)}/content`,
        });
      },
      create: ({
        item,
        projectId,
      }: {
        item: CreateRegistryItemInput;
        projectId: string;
      }): Promise<RegistryCreateItemOutput> => {
        return request<RegistryCreateItemOutput>({
          method: "POST",
          path: "/api/v1/registry/items",
          body: { item, projectId },
        });
      },
      getPreview: ({
        id,
      }: {
        id: string;
      }): Promise<RegistryItemPreviewResult> => {
        return request<RegistryItemPreviewResult>({
          method: "GET",
          path: `/api/v1/registry/items/${encodeURIComponent(id)}/preview`,
        });
      },
      getOwnerAffiliateLink: ({
        id,
      }: {
        id: string;
      }): Promise<{ affiliateLink: string | null }> => {
        return request<{ affiliateLink: string | null }>({
          method: "GET",
          path: `/api/v1/registry/items/${encodeURIComponent(id)}/owner-affiliate-link`,
        });
      },
    },
  };
}

export type RegistryClient = ReturnType<typeof createRegistryClient>;
